import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import moment from 'moment';
import {
    getClientsList,
    getBillFilterHeader,
    formatDateForApi,
    exportCounterProducts,
    exportCounterCategories,
    exportCounterItemReport,
    exportCounterCategoryReport,
    exportOrdersProducts,
    exportOrdersCategories,
    exportOrdersCustomersProducts,
    exportOrdersItemReport,
    exportOrdersCategoryReport,
    exportOrdersCustomerReport
} from '../utilities/api';
import { exportStyledExcel } from '../utilities/excelExport';

const formatDate = (d) => (moment(d).isValid() ? moment(d).format('DD-MM-YYYY') : d || '-');
const num = (v) => parseFloat(v || 0);

const counterProductsColumns = [
    { header: 'Product ID', getValue: (r) => r.pr_id || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Product Code', getValue: (r) => r.product_code || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Tax %', getValue: (r) => num(r.basic_tax_percent), numeric: true, skipSum: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Discount %', getValue: (r) => num(r.discount_perc), numeric: true, skipSum: true },
    { header: 'Type', getValue: (r) => r.product_type || '-' },
    { header: 'Status', getValue: (r) => r.product_status || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' }
];

const categoryColumns = [
    { header: 'Category ID', getValue: (r) => r.prcat_cat_id || '-' },
    { header: 'Category Name', getValue: (r) => r.prcat_cat_name || '-' },
    { header: 'Reference Name', getValue: (r) => r.prcat_ref_name || '-' },
    { header: 'Discount %', getValue: (r) => num(r.prcat_discount), numeric: true, skipSum: true },
    { header: 'Status', getValue: (r) => r.prcat_status || '-' }
];

const itemReportColumns = [
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Qty', getValue: (r) => num(r.total_qty), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.discount_amount), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amount), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amount), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_amount), numeric: true }
];

const ordersItemReportColumns = [
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Qty', getValue: (r) => num(r.total_qty), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.discount_amount), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amount), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amount), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_amount), numeric: true }
];

const categoryReportColumns = [
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Qty', getValue: (r) => num(r.total_qty), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amount), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amount), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_amount), numeric: true }
];

const ordersProductsColumns = [
    { header: 'Product ID', getValue: (r) => r.spl_id || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Product No', getValue: (r) => r.product_no || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Tax %', getValue: (r) => num(r.basic_tax_percent), numeric: true, skipSum: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Status', getValue: (r) => r.product_status || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' }
];

const ordersCustomersProductsColumns = [
    { header: 'Customer', getValue: (r) => r.company_name || '-' },
    { header: 'Phone', getValue: (r) => r.phone_number || '-' },
    { header: 'GST No', getValue: (r) => r.gst_no || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Status', getValue: (r) => r.product_status || '-' }
];

const ordersCustomerReportColumns = [
    { header: 'Bill ID', getValue: (r) => r.bill_id || '-' },
    { header: 'Invoice No', getValue: (r) => r.invoice_no || '-' },
    { header: 'Date', getValue: (r) => formatDate(r.invoice_date) },
    { header: 'Time', getValue: (r) => r.invoice_time || '-' },
    { header: 'Order Type', getValue: (r) => r.order_type || '-' },
    { header: 'Customer', getValue: (r) => r.company_name || '-' },
    { header: 'Phone', getValue: (r) => r.phone_number || '-' },
    { header: 'GST No', getValue: (r) => r.gst_no || '-' },
    { header: 'Basic', getValue: (r) => num(r.total_basic_price), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.disc_total_amt), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_aft_disc), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_paid_amount), numeric: true },
    { header: 'Balance', getValue: (r) => num(r.balance_amount), numeric: true },
    { header: 'MOP', getValue: (r) => r.final_mop || '-' },
    { header: 'Status', getValue: (r) => r.status || '-' },
    { header: 'Settlement Status', getValue: (r) => r.stlmnt_status || '-' }
];

const COUNTER_MASTERS = [
    { key: 'counter_products', label: 'Products', description: 'Raw product data (server database), with category name.', needsDate: false, fetchFn: exportCounterProducts, columns: counterProductsColumns, sheetName: 'Counter Products' },
    { key: 'counter_categories', label: 'Category', description: 'Raw product category data (server database).', needsDate: false, fetchFn: exportCounterCategories, columns: categoryColumns, sheetName: 'Counter Categories' }
];

const COUNTER_REPORTS = [
    { key: 'counter_item_report', label: 'Item Report', description: 'Sales grouped by product name for the selected date range.', needsDate: true, fetchFn: exportCounterItemReport, columns: itemReportColumns, sheetName: 'Counter Item Report' },
    { key: 'counter_category_report', label: 'Category Report', description: 'Sales grouped by category name for the selected date range.', needsDate: true, fetchFn: exportCounterCategoryReport, columns: categoryReportColumns, sheetName: 'Counter Category Report' }
];

const ORDERS_MASTERS = [
    { key: 'orders_products', label: 'Products', description: 'Raw order product data (client database), with category name.', needsDate: false, fetchFn: exportOrdersProducts, columns: ordersProductsColumns, sheetName: 'Orders Products' },
    { key: 'orders_categories', label: 'Category', description: 'Raw product category data (client database).', needsDate: false, fetchFn: exportOrdersCategories, columns: categoryColumns, sheetName: 'Orders Categories' },
    { key: 'orders_customers_products', label: 'Customers with Products', description: 'Order products joined with their assigned customer.', needsDate: false, fetchFn: exportOrdersCustomersProducts, columns: ordersCustomersProductsColumns, sheetName: 'Customers with Products' }
];

const ORDERS_REPORTS = [
    { key: 'orders_item_report', label: 'Item Report', description: 'Sales grouped by product name for the selected date range (Institution + ODC).', needsDate: true, fetchFn: exportOrdersItemReport, columns: ordersItemReportColumns, sheetName: 'Orders Item Report' },
    { key: 'orders_category_report', label: 'Category Report', description: 'Sales grouped by category name for the selected date range (Institution + ODC).', needsDate: true, fetchFn: exportOrdersCategoryReport, columns: categoryReportColumns, sheetName: 'Orders Category Report' },
    { key: 'orders_customer_report', label: 'Customer wise Report', description: 'Bill list with customer details for the selected date range (same shape as Bill Report).', needsDate: true, fetchFn: exportOrdersCustomerReport, columns: ordersCustomerReportColumns, sheetName: 'Orders Customer Report' }
];

const ExportRow = ({ def, disabled, loadingKey, onDownload }) => (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2 py-2 excel-export-row">
        <div>
            <div className="font-semibold">{def.label}</div>
            <div className="text-color-secondary text-sm">{def.description}</div>
        </div>
        <Button label="Download" icon="pi pi-download" className="p-button-sm p-button-outlined" disabled={disabled} loading={loadingKey === def.key} onClick={() => onDownload(def)} />
    </div>
);

const ExcelExport = () => {
    const toast = useRef(null);

    const role = (localStorage.getItem('role') || '').toUpperCase();
    const isGspos = role === 'GSPOS';
    const isManager = role === 'MANAGER';
    const ownClientId = localStorage.getItem('clientid') || '';
    const ownBranchId = localStorage.getItem('branchid') || '';
    const ownBranchName = localStorage.getItem('branch') || ownBranchId;
    const managerBranchInit = useRef(false);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [downloadingKey, setDownloadingKey] = useState(null);

    const effectiveClientId = isGspos ? selectedClient?.client_id || '' : ownClientId;
    const effectiveClientLabel = isGspos ? selectedClient?.client_name || '' : '';

    useEffect(() => {
        if (!isGspos) return;
        getClientsList()
            .then(setClients)
            .catch(() => setClients([]));
    }, [isGspos]);

    useEffect(() => {
        if (!effectiveClientId) {
            setBranches([]);
            return;
        }
        getBillFilterHeader({ rolename: role || 'CEO', clientname: effectiveClientId })
            .then((res) => {
                const raw = Array.isArray(res?.branchs) ? res.branchs : [];
                const hasAll = raw.some((b) => String(b?.branch_id).toLowerCase() === 'all');
                setBranches(hasAll ? raw : [{ branch_id: 'All', branch_name: 'All Branches' }, ...raw]);
            })
            .catch(() => setBranches([]));
    }, [effectiveClientId, role]);

    // MANAGER: branch is hidden, always the manager's own branch
    useEffect(() => {
        if (!isManager || managerBranchInit.current || !ownBranchId) return;
        setSelectedBranch({ branch_id: ownBranchId, branch_name: ownBranchName });
        managerBranchInit.current = true;
    }, [isManager, ownBranchId, ownBranchName]);

    // Reset branch selection whenever the client changes (GSPOS switching clients)
    useEffect(() => {
        if (isManager) return;
        setSelectedBranch(null);
    }, [effectiveClientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = async (def) => {
        if (!effectiveClientId) {
            toast.current?.show({ severity: 'warn', summary: 'Client required', detail: 'Please select a client first.', life: 4000 });
            return;
        }
        if (def.needsDate && (!fromDate || !toDate)) {
            toast.current?.show({ severity: 'warn', summary: 'Date range required', detail: 'Please select from and to date for this report.', life: 4000 });
            return;
        }

        setDownloadingKey(def.key);
        try {
            const payload = { client_id: effectiveClientId, branch_id: selectedBranch?.branch_id || '' };
            if (def.needsDate) {
                payload.from_date = formatDateForApi(fromDate);
                payload.to_date = formatDateForApi(toDate);
            }
            const rows = await def.fetchFn(payload);
            if (!rows.length) {
                toast.current?.show({ severity: 'info', summary: 'No data', detail: 'No records found for the selected filters.', life: 4000 });
                return;
            }

            const filterLines = [];
            if (def.needsDate) filterLines.push(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`);
            if (effectiveClientLabel) filterLines.push(`Client: ${effectiveClientLabel}`);
            filterLines.push(`Branch: ${selectedBranch?.branch_name || 'All Branches'}`);
            filterLines.push(`Generated On: ${new Date().toLocaleString('en-IN')}`);

            await exportStyledExcel({
                fileName: `${def.sheetName.replace(/\s+/g, '')}_${moment().format('YYYY-MM-DD')}.xlsx`,
                sheetName: def.sheetName,
                title: def.label,
                filterLines,
                columns: def.columns,
                rows
            });
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Export failed', detail: err?.response?.data?.message || err?.message || 'Failed to export data', life: 5000 });
        } finally {
            setDownloadingKey(null);
        }
    };

    const rowsDisabled = !effectiveClientId;

    return (
        <div className="excel-export-container">
            <Toast ref={toast} />

            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Excel Export
                </h2>
                <p className="text-color-secondary">Download raw master data and reports as Excel, based on client and branch.</p>
            </div>

            <Card className="mb-3">
                <div className="flex flex-wrap align-items-end gap-3">
                    {isGspos && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="client">Client</label>
                            <Dropdown id="client" value={selectedClient} options={clients} onChange={(e) => setSelectedClient(e.value)} optionLabel="client_name" placeholder="Select Client" className="w-full" filter showClear />
                        </div>
                    )}

                    {!isManager && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown
                                id="branch"
                                value={selectedBranch}
                                options={branches}
                                onChange={(e) => setSelectedBranch(e.value)}
                                optionLabel="branch_name"
                                placeholder="All Branches"
                                className="w-full"
                                filter
                                showClear
                                disabled={!effectiveClientId}
                            />
                        </div>
                    )}

                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '150px' }}>
                        <label htmlFor="fromDate">
                            From Date <span className="text-color-secondary text-sm">(for Reports)</span>
                        </label>
                        <Calendar id="fromDate" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '150px' }}>
                        <label htmlFor="toDate">
                            To Date <span className="text-color-secondary text-sm">(for Reports)</span>
                        </label>
                        <Calendar id="toDate" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                    </div>
                </div>
            </Card>

            <Card className="mb-3">
                <h3 className="text-xl font-semibold mt-0 mb-1">Counter Section</h3>
                <p className="text-color-secondary text-sm mt-0">Dine-in / counter sales data.</p>

                <Divider />
                <h4 className="text-md font-semibold mb-2">Masters</h4>
                {COUNTER_MASTERS.map((def) => (
                    <ExportRow key={def.key} def={def} disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                ))}

                <Divider />
                <h4 className="text-md font-semibold mb-2">Reports</h4>
                {COUNTER_REPORTS.map((def) => (
                    <ExportRow key={def.key} def={def} disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                ))}
            </Card>

            <Card>
                <h3 className="text-xl font-semibold mt-0 mb-1">Orders Section</h3>
                <p className="text-color-secondary text-sm mt-0">Institution Sales + ODC (Outdoor Catering) combined.</p>

                <Divider />
                <h4 className="text-md font-semibold mb-2">Masters</h4>
                {ORDERS_MASTERS.map((def) => (
                    <ExportRow key={def.key} def={def} disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                ))}

                <Divider />
                <h4 className="text-md font-semibold mb-2">Reports</h4>
                {ORDERS_REPORTS.map((def) => (
                    <ExportRow key={def.key} def={def} disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                ))}
            </Card>
        </div>
    );
};

export default ExcelExport;
